import { aborted, blocked, failed, partial } from 'flare-chat-core/contracts/chat/operation-result';
import type { ChatOperationResult } from 'flare-chat-core/contracts';

interface Receipt<Request, Value> {
  request: Request;
  acknowledgment?: Value;
  settled?: boolean;
}

/** Instance submission receipts only: no stream, session or canonical state engine. */
export const createSubmissionState = <Request, Value>() => {
  const receipts = new Map<string, Receipt<Request, Value>>();
  let pending = false;
  const execute = async ({ key, request, submit, refresh, isCurrent, operation }: {
    key: string; request: Request;
    submit: (input: Request) => Promise<ChatOperationResult<Value>>;
    refresh: (value: Value) => Promise<void>;
    isCurrent: () => boolean; operation: string;
  }): Promise<ChatOperationResult<Value>> => {
    if (!isCurrent()) return aborted();
    if (pending) return blocked('submission_pending');
    pending = true;
    const receipt = receipts.get(key) ?? { request };
    receipts.set(key, receipt);
    try {
      const result = receipt.acknowledgment === undefined
        ? await submit(receipt.request)
        : { status: 'completed' as const, value: receipt.acknowledgment };
      if (result.status !== 'completed') return result;
      receipt.acknowledgment = result.value;
      if (!isCurrent()) return aborted(result.value);
      if (receipt.settled) return result;
      await refresh(result.value);
      if (!isCurrent()) return aborted(result.value);
      receipt.settled = true;
      return result;
    } catch (error) {
      return receipt.acknowledgment === undefined
        ? failed(error, operation)
        : partial(receipt.acknowledgment, error, 'refreshSession');
    } finally {
      pending = false;
    }
  };
  return { execute };
};
